import { Holiday } from "../models/leaveModels";
import { addDays, parseISODate, toISODate } from "./dateUtils";

/**
 * Working-day helpers. A working day is Mon–Fri and not a non-working holiday
 * ("Special Working" holidays still count as working days).
 *
 * (Descends from the guide's Phase 4.6 routine, hardened against its
 * toISOString() timezone bug — see dateUtils.)
 */

/** YYYY-MM-DD dates of the holidays that are days off. */
export function nonWorkingHolidayDates(holidays: Holiday[]): Set<string> {
  return new Set(
    holidays
      .filter((h) => h.type !== "Special Working")
      .map((h) => h.date.slice(0, 10)),
  );
}

export function isWeekend(iso: string): boolean {
  const day = parseISODate(iso).getDay(); // 0 = Sun, 6 = Sat
  return day === 0 || day === 6;
}

export function isWorkingDay(iso: string, holidayDates: Set<string>): boolean {
  return !isWeekend(iso) && !holidayDates.has(iso);
}

/**
 * The |n|th working day after (n > 0) or before (n < 0) `fromISO`, which is
 * itself never counted. n = 0 returns `fromISO` unchanged.
 * e.g. Friday + 3 -> the following Wednesday.
 */
export function addWorkingDays(fromISO: string, n: number, holidays: Holiday[]): string {
  const holidayDates = nonWorkingHolidayDates(holidays);
  const step = n < 0 ? -1 : 1;
  let remaining = Math.abs(n);
  let current = fromISO;
  while (remaining > 0) {
    current = addDays(current, step);
    if (isWorkingDay(current, holidayDates)) remaining--;
  }
  return current;
}

/** Working days in (fromISO, toISO] — 0 when toISO is not after fromISO. */
export function workingDaysUntil(fromISO: string, toISO: string, holidays: Holiday[]): number {
  if (toISO <= fromISO) return 0;
  const holidayDates = nonWorkingHolidayDates(holidays);
  let count = 0;
  for (let d = addDays(fromISO, 1); d <= toISO; d = addDays(d, 1)) {
    if (isWorkingDay(d, holidayDates)) count++;
  }
  return count;
}

/** Every working day in [startDate, endDate] inclusive, as YYYY-MM-DD. */
export function workingDaysInRange(
  startDate: string,
  endDate: string,
  holidays: Holiday[],
): string[] {
  if (!startDate || !endDate || endDate < startDate) return [];
  const holidayDates = nonWorkingHolidayDates(holidays);
  const dates: string[] = [];
  const current = parseISODate(startDate);
  const end = parseISODate(endDate);
  while (current <= end) {
    const iso = toISODate(current);
    if (isWorkingDay(iso, holidayDates)) dates.push(iso);
    current.setDate(current.getDate() + 1);
  }
  return dates;
}
